import {
  BarElement,
  BubbleController,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  type ChartOptions,
  type ScriptableLineSegmentContext,
} from "chart.js";
import { Bar, Bubble, Line } from "react-chartjs-2";

import { HEALTH_COLORS } from "./accountManagementShared";
import { CarespaceCountLink } from "./carespaceCountLink";
import { ChartPanel } from "./dashboardKit";
import { quadrantZonesPlugin, type QuadrantZonesOptions } from "./dashboardQuadrantZones";
import type { RespiteHsaBuckets } from "./respiteMetrics";
import type { RespiteSummaryResponse } from "./respiteTypes";
import type { RespiteView } from "./respiteView";

ChartJS.register(
  BarElement,
  BubbleController,
  CategoryScale,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
);

const RATE_COLOR = "#2E8B57";
const REFERENCE_COLOR = "#898781";
const GRID_COLOR = "#eee9df";
/** How far below the overall rate a provider can sit before it reads as at risk. */
const WATCH_BAND = 0.1;

/**
 * HSA buckets grouped for charts. Order runs from done to not wanted; the four chromatic
 * slots pass the categorical validator (light), and "Not requested" is a deliberate neutral.
 */
export const HSA_GROUPS: Array<{
  key: string;
  label: string;
  color: string;
  value: (row: RespiteHsaBuckets) => number;
}> = [
  { key: "onTime", label: "Done ≤60 days", color: "#008300", value: (row) => row.completedOnTime },
  { key: "late", label: "Done >60 days", color: "#2a78d6", value: (row) => row.completedLate },
  {
    key: "inWindow",
    label: "Requested, within 60 days",
    color: "#eda100",
    value: (row) => row.openInWindow,
  },
  {
    key: "overdue",
    label: "Requested, overdue",
    color: "#e34948",
    value: (row) => row.openOverdue,
  },
  {
    key: "notRequested",
    label: "Not requested",
    color: "#c3c2b7",
    value: (row) => row.notRequested,
  },
];

function pct(value: number | null, digits = 0): string {
  return value === null ? "—" : `${(value * 100).toFixed(digits)}%`;
}

function monthLabel(month: string): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    year: "2-digit",
    timeZone: "UTC",
  }).format(new Date(`${month}-01T00:00:00Z`));
}

function shortDay(isoDate: string): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${isoDate}T00:00:00Z`));
}

function dayRange(from: string, to: string): string {
  return `${shortDay(from)} – ${shortDay(to)}`;
}

/** The latest month whose 60-day windows have all closed, and the change from the one before. */
export function latestClosedMonth<T extends RespiteHsaBuckets>(months: T[]) {
  const closed = months.filter((row) => row.openInWindow === 0 && row.goalRate !== null);
  const latest = closed.at(-1);
  const prior = closed.at(-2);
  return {
    latest: latest ?? null,
    change:
      latest?.goalRate != null && prior?.goalRate != null ? latest.goalRate - prior.goalRate : null,
  };
}

function median(values: number[]): number {
  const sorted = [...values].sort((left, right) => left - right);
  const middle = Math.floor(sorted.length / 2);
  if (!sorted.length) return 0;
  return sorted.length % 2 ? sorted[middle]! : (sorted[middle - 1]! + sorted[middle]!) / 2;
}

export function providerStanding(rate: number, overall: number): "healthy" | "watch" | "at-risk" {
  if (rate >= overall) return "healthy";
  return rate >= overall - WATCH_BAND ? "watch" : "at-risk";
}

const STANDING_LABELS = {
  healthy: "At or above overall",
  watch: `Within ${WATCH_BAND * 100} pts below`,
  "at-risk": `More than ${WATCH_BAND * 100} pts below`,
} as const;

/** Hero: is the Medicare 60-day completion rate going up, month by enrollment month? */
export function HsaRateTrend(props: { summary: RespiteSummaryResponse; view: RespiteView }) {
  const { toDate } = props.summary;
  const { due, hsa } = props.view;
  const { latest, change } = latestClosedMonth(props.view.months);
  const months = props.view.months.filter((row) => row.goalRate !== null || row.openInWindow > 0);
  const stillOpen = (index: number | undefined) =>
    index !== undefined && (months[index]?.openInWindow ?? 0) > 0;

  const data = {
    labels: months.map((row) => monthLabel(row.month)),
    datasets: [
      {
        label: "Done within 60 days",
        data: months.map((row) => (row.goalRate === null ? null : row.goalRate * 100)),
        borderColor: RATE_COLOR,
        backgroundColor: "rgba(46, 139, 87, 0.10)",
        fill: "origin" as const,
        borderWidth: 2,
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBorderWidth: 2,
        pointBorderColor: RATE_COLOR,
        pointBackgroundColor: months.map((_, index) => (stillOpen(index) ? "#ffffff" : RATE_COLOR)),
        segment: {
          borderDash: (context: ScriptableLineSegmentContext) =>
            stillOpen(context.p1DataIndex) ? [5, 4] : undefined,
        },
        spanGaps: true,
      },
      {
        label: `Window overall (${pct(hsa.goalRate)})`,
        data: months.map(() => (hsa.goalRate === null ? null : hsa.goalRate * 100)),
        borderColor: REFERENCE_COLOR,
        borderWidth: 1.5,
        borderDash: [3, 3],
        pointRadius: 0,
        pointHoverRadius: 0,
        fill: false,
      },
    ],
  };
  const options: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: {
        position: "bottom",
        labels: { usePointStyle: true, boxWidth: 8, font: { size: 11 } },
      },
      tooltip: {
        filter: (item) => item.datasetIndex === 0,
        callbacks: {
          label: (item) => `${Number(item.raw).toFixed(1)}% done within 60 days`,
          afterLabel: (item) => {
            const row = months[item.dataIndex];
            if (!row) return "";
            const lines = [`${row.completedOnTime} of ${row.due} due`];
            if (row.openInWindow) lines.push(`${row.openInWindow} still within 60 days`);
            return lines;
          },
        },
      },
    },
    scales: {
      x: { grid: { display: false } },
      y: {
        min: 0,
        max: 100,
        ticks: { stepSize: 25, callback: (value) => `${value}%` },
        grid: { color: GRID_COLOR },
      },
    },
  };

  return (
    <section className="rounded-lg border border-border/70 bg-background/80 p-4 shadow-xs">
      <div className="grid gap-4 lg:grid-cols-[minmax(0,19rem)_minmax(0,1fr)]">
        <div className="flex flex-col justify-between gap-4">
          <div>
            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Medicare 60-day home safety · to date
            </span>
            <p className="mt-1 text-5xl font-semibold tracking-tight text-foreground">
              {pct(due.goalRate)}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              of HSAs whose 60-day deadline fell {dayRange(toDate.deadlineFrom, toDate.deadlineTo)}{" "}
              were done in time (
              <CarespaceCountLink
                count={due.completedOnTime}
                recordIds={due.ids.completedOnTime}
                label="HSAs done within 60 days"
              />{" "}
              of <CarespaceCountLink count={due.due} recordIds={due.ids.due} label="HSAs due" />)
            </p>
          </div>
          {latest ? (
            <div className="rounded-md border border-border/70 bg-muted/30 px-3 py-2 text-sm">
              <p className="text-xs text-muted-foreground">
                Latest closed month · {monthLabel(latest.month)}
              </p>
              <p className="font-semibold text-foreground">
                {pct(latest.goalRate)}
                {change !== null ? (
                  <span
                    className={
                      change >= 0 ? "ml-2 text-xs text-emerald-700" : "ml-2 text-xs text-red-600"
                    }
                  >
                    {change >= 0 ? "▲" : "▼"} {Math.abs(change * 100).toFixed(0)} pts vs prior month
                  </span>
                ) : null}
              </p>
            </div>
          ) : null}
          <p className="text-xs text-muted-foreground">
            In that group:{" "}
            <CarespaceCountLink
              count={due.openOverdue}
              recordIds={due.ids.openOverdue}
              label="HSAs missed and still open"
            />{" "}
            missed and still open ·{" "}
            <CarespaceCountLink
              count={due.completedLate}
              recordIds={due.ids.completedLate}
              label="HSAs done late"
            />{" "}
            done late
          </p>
        </div>
        <div className="relative h-64 min-w-0">
          {months.length ? (
            <Line data={data as never} options={options} />
          ) : (
            <p className="py-16 text-center text-sm text-muted-foreground">
              No HSAs were due for enrollments in this window.
            </p>
          )}
        </div>
      </div>
      <small className="mt-2 block text-xs text-muted-foreground">
        The headline counts every carespace whose deadline (initial PAF + 60 days) passed in the
        last 60 days, so it is final and rolls forward daily. Each chart point is the same rate for
        carespaces enrolled that month; dashed segments and hollow points mark months with HSAs
        still inside their 60 days, so the rate there can still change.
      </small>
    </section>
  );
}

/** Which providers are behind on the 60-day goal, and which carry the overdue backlog. */
export function HsaProviderQuadrant(props: { view: RespiteView }) {
  type Group = RespiteView["hsaGroups"][number];
  const overall = props.view.hsa.goalRate;
  const providers = props.view.hsaGroups.filter(
    (row): row is Group & { goalRate: number } =>
      row.goalRate !== null && row.providerGroup !== "No referral",
  );
  const unattributed = props.view.hsaGroups.find((row) => row.providerGroup === "No referral");

  if (overall === null || !providers.length) {
    return (
      <ChartPanel title="Provider standing">
        <p className="py-8 text-center text-sm text-muted-foreground">
          No provider has HSAs due in this window yet.
        </p>
      </ChartPanel>
    );
  }

  const maximumDue = Math.max(...providers.map((row) => row.due), 1);
  const radiusScale = 22 / Math.sqrt(maximumDue);
  const overdueDivider = median(providers.map((row) => row.openOverdue));
  const standings = Object.keys(STANDING_LABELS) as Array<keyof typeof STANDING_LABELS>;
  const data = {
    datasets: standings.map((standing) => ({
      label: STANDING_LABELS[standing],
      data: providers
        .filter((row) => providerStanding(row.goalRate, overall) === standing)
        .map((row) => ({
          x: row.goalRate * 100,
          y: row.openOverdue,
          r: Math.max(Math.sqrt(row.due) * radiusScale, 4),
          provider: row,
        })),
      backgroundColor: `${HEALTH_COLORS[standing]}b8`,
      borderColor: HEALTH_COLORS[standing],
      borderWidth: 1.5,
    })),
  };
  const zones: QuadrantZonesOptions = {
    x: overall * 100,
    y: overdueDivider,
    xLabel: `OVERALL ${pct(overall)}`,
    yLabel: "MEDIAN OVERDUE",
    topLeft: { label: "FIX NOW", fill: "rgba(220, 76, 53, .10)" },
    topRight: { label: "BACKLOG", fill: "rgba(245, 158, 11, .07)" },
    bottomLeft: { label: "BEHIND", fill: "rgba(220, 76, 53, .05)" },
    bottomRight: { label: "ON TRACK", fill: "rgba(16, 185, 129, .06)" },
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 8, right: 8 } },
    interaction: { mode: "nearest", intersect: true },
    plugins: {
      quadrantZones: zones,
      legend: {
        position: "bottom",
        labels: { usePointStyle: true, boxWidth: 9, font: { size: 11 } },
      },
      tooltip: {
        callbacks: {
          title: (items: Array<{ raw?: unknown }>) =>
            (items[0]?.raw as { provider?: Group } | undefined)?.provider?.providerGroup ?? "",
          label: (item: { raw?: unknown }) => {
            const row = (item.raw as { provider: Group & { goalRate: number } }).provider;
            return [
              `Done within 60 days: ${pct(row.goalRate, 1)} (${row.completedOnTime} of ${row.due})`,
              `Overdue: ${row.openOverdue}`,
              `Still within 60 days: ${row.openInWindow}`,
              `Carespaces: ${row.cohort}`,
              ...(row.providers.length > 1 ? [`Sites: ${row.providers.length}`] : []),
            ];
          },
        },
      },
    },
    scales: {
      x: {
        min: 0,
        max: 100,
        title: { display: true, text: "Done within 60 days" },
        ticks: { callback: (value: string | number) => `${value}%` },
        grid: { color: GRID_COLOR },
      },
      y: {
        suggestedMin: 0,
        suggestedMax: 4,
        grace: "20%",
        title: { display: true, text: "Requested HSAs overdue" },
        ticks: {
          precision: 0,
          callback: (value: string | number) => (Number(value) < 0 ? "" : `${value}`),
        },
        grid: { color: GRID_COLOR },
      },
    },
  };

  return (
    <section className="rounded-lg border border-border/70 bg-background/80 p-4 shadow-xs">
      <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
        <div>
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            HSA providers
          </span>
          <h2 className="text-sm font-semibold text-foreground">Provider standing</h2>
        </div>
        <span className="text-xs text-muted-foreground">
          x = done within 60 days · y = overdue · area = HSAs due
        </span>
      </div>
      <p className="mb-3 text-sm text-muted-foreground">
        Providers in the upper-left are below the overall rate and holding the most overdue HSAs:
        follow up there first.
      </p>
      <div className="relative h-[380px] min-w-0">
        <Bubble data={data as never} plugins={[quadrantZonesPlugin]} options={options as never} />
      </div>
      <small className="mt-2 block text-xs text-muted-foreground">
        Fill compares each provider with the overall rate. Bubble area is proportional to HSAs due.
        {unattributed?.due
          ? ` ${unattributed.due} due HSAs have no referral and are not plotted.`
          : ""}
      </small>
    </section>
  );
}

/** Composition of each enrollment month's cohort, from done to not requested. */
export function HsaMonthlyChart(props: { view: RespiteView }) {
  const months = props.view.months;
  const data = {
    labels: months.map((row) => monthLabel(row.month)),
    datasets: HSA_GROUPS.map((group) => ({
      label: group.label,
      data: months.map(group.value),
      backgroundColor: group.color,
      borderColor: "#ffffff",
      borderWidth: { top: 2 },
      borderSkipped: false as const,
      stack: "hsa",
    })),
  };
  const options: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom", labels: { boxWidth: 12, font: { size: 11 } } },
      tooltip: {
        mode: "index",
        callbacks: {
          footer: (items) => {
            const row = months[items[0]?.dataIndex ?? -1];
            return row ? `Done within 60 days: ${pct(row.goalRate, 1)}` : "";
          },
        },
      },
    },
    scales: {
      x: { stacked: true, grid: { display: false } },
      y: { stacked: true, beginAtZero: true, ticks: { precision: 0 }, grid: { color: GRID_COLOR } },
    },
  };
  return (
    <ChartPanel title="Where each enrollment month stands">
      {months.length ? (
        <div className="relative h-72">
          <Bar data={data} options={options} />
        </div>
      ) : (
        <p className="py-8 text-center text-sm text-muted-foreground">
          No aligned, respite-eligible carespaces had an initial PAF in this window.
        </p>
      )}
    </ChartPanel>
  );
}
